// src/app/settings/application/work-orders/work-order-numbers.service.ts

import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import {
  Observable,
  catchError,
  expand,
  map,
  of,
  reduce,
  throwError,
} from 'rxjs';

import { environment } from '../../../../environments/environment';

import {
  CreateWorkOrderNumberPayload,
  Department,
  DepartmentsResponse,
  Project,
  ProjectsResponse,
  UpdateWorkOrderNumberPayload,
  WorkOrderNumberFilters,
  WorkOrderNumbersResponse,
} from '../models/work-order-numbers.model';

@Injectable({
  providedIn: 'root',
})
export class WorkOrderNumbersService {
  private readonly baseUrl = environment.apiBaseUrl;
  private readonly workOrderNumbersUrl =
    `${this.baseUrl}/api/work-order-numbers`;

  constructor(private readonly http: HttpClient) {}

  // ---------------------------------------
  // Get WONs
  // ---------------------------------------
  getWorkOrderNumbers(
    pageNumber = 1,
    pageSize = 20,
    filters?: WorkOrderNumberFilters,
  ): Observable<WorkOrderNumbersResponse | null> {
    const params = this.buildWorkOrderNumberParams(
      pageNumber,
      pageSize,
      filters,
    );

    return this.http
      .get<WorkOrderNumbersResponse>(
        this.workOrderNumbersUrl,
        { params },
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error(
            'Error fetching work order numbers:',
            {
              status: error.status,
              response: error.error,
              message: error.message,
            },
          );

          return of(null);
        }),
      );
  }

  // ---------------------------------------
  // Create WON
  // ---------------------------------------
  createWorkOrderNumber(
    payload: CreateWorkOrderNumberPayload,
  ): Observable<void> {
    return this.http
      .post<void>(
        this.workOrderNumbersUrl,
        payload,
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error(
            'Error creating work order number:',
            {
              status: error.status,
              response: error.error,
              payload,
              message: error.message,
            },
          );

          return throwError(() => error);
        }),
      );
  }

  // ---------------------------------------
  // Update WON
  // ---------------------------------------
  updateWorkOrderNumber(
    id: number,
    payload: UpdateWorkOrderNumberPayload,
  ): Observable<void> {
    return this.http
      .patch<void>(
        `${this.workOrderNumbersUrl}/${id}`,
        payload,
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error(
            'Error updating work order number:',
            {
              status: error.status,
              response: error.error,
              payload,
              message: error.message,
            },
          );

          return throwError(() => error);
        }),
      );
  }

  // ---------------------------------------
  // Get all projects
  // ---------------------------------------
  getAllProjects(): Observable<Project[]> {
    return this.fetchAllPages<
      ProjectsResponse,
      Project
    >(`${this.baseUrl}/api/projects/all`);
  }

  // ---------------------------------------
  // Get all departments
  // ---------------------------------------
  getAllDepartments(): Observable<Department[]> {
    return this.fetchAllPages<
      DepartmentsResponse,
      Department
    >(`${this.baseUrl}/api/departments`);
  }

  // ---------------------------------------
  // Build query parameters
  // ---------------------------------------
  private buildWorkOrderNumberParams(
    pageNumber: number,
    pageSize: number,
    filters?: WorkOrderNumberFilters,
  ): HttpParams {
    let params = new HttpParams()
      .set('pageNumber', String(pageNumber))
      .set('pageSize', String(pageSize));

    const departmentIds =
      filters?.departmentIds?.filter(
        (id) =>
          Number.isFinite(id) &&
          id > 0,
      ) ?? [];

    const projectId =
      typeof filters?.projectId === 'number' &&
      Number.isFinite(filters.projectId) &&
      filters.projectId > 0
        ? filters.projectId
        : null;

    const isOverhead =
      typeof filters?.isOverhead === 'boolean'
        ? filters.isOverhead
        : null;

    const searchTerm = String(
      filters?.searchTerm ?? '',
    ).trim();

    if (departmentIds.length > 0) {
      params = params.set(
        'departmentIds',
        departmentIds.join(','),
      );
    }

    if (projectId !== null) {
      params = params.set(
        'projectId',
        String(projectId),
      );
    }

    if (isOverhead !== null) {
      params = params.set(
        'isOverhead',
        String(isOverhead),
      );
    }

    if (searchTerm) {
      params = params.set(
        'searchTerm',
        searchTerm,
      );
    }

    return params;
  }

  // ---------------------------------------
  // Generic pagination helper
  // ---------------------------------------
  private fetchAllPages<
    T extends {
      currentPage: number;
      totalPages: number;
      data: K[];
    },
    K,
  >(url: string): Observable<K[]> {
    const pageSize = 50;

    const buildParams = (
      pageNumber: number,
    ): HttpParams =>
      new HttpParams()
        .set('pageNumber', String(pageNumber))
        .set('pageSize', String(pageSize));

    return this.http
      .get<T>(url, {
        params: buildParams(1),
      })
      .pipe(
        expand((response) => {
          if (
            response.currentPage >=
            response.totalPages
          ) {
            return of();
          }

          return this.http.get<T>(url, {
            params: buildParams(
              response.currentPage + 1,
            ),
          });
        }),
        map((response) => response.data),
        reduce(
          (allItems, pageItems) => [
            ...allItems,
            ...pageItems,
          ],
          [] as K[],
        ),
        catchError(
          (error: HttpErrorResponse) => {
            console.error(
              'Error fetching paginated data:',
              {
                status: error.status,
                response: error.error,
                message: error.message,
              },
            );

            return of([]);
          },
        ),
      );
  }
}